import React from "react";
import {
    Page,
    Text,
    View,
    Document,
    StyleSheet,
    Image,
    Font,
} from "@react-pdf/renderer";
import stojaLogo from "../assets/images/racuni/stoja-logo.png";
import certificationLogo from "../assets/images/racuni/certification.png";
import dqsLogo from "../assets/images/racuni/dqs-logo.png";
import stojaStamp from "../assets/images/racuni/stoja-stamp.png";

import robotoBold from "../assets/fonts/roboto/Roboto-Bold.ttf";
import robotoBoldItalic from "../assets/fonts/roboto/Roboto-BoldItalic.ttf";
import robotoItalic from "../assets/fonts/roboto/Roboto-Italic.ttf";
import robotoRegular from "../assets/fonts/roboto/Roboto-Regular.ttf";

// Register fonts that support Central European characters
Font.register({
    family: "Roboto",
    format: "truetype",
    fonts: [
        {
            src: robotoRegular,
            fontWeight: 400,
            fontStyle: "normal",
        },
        {
            src: robotoBold,
            fontWeight: 700,
            fontStyle: "normal",
        },
        {
            src: robotoItalic,
            fontWeight: 400,
            fontStyle: "italic",
        },
        {
            src: robotoBoldItalic,
            fontWeight: 700,
            fontStyle: "italic",
        },
    ],
});

const styles = StyleSheet.create({
    page: {
        padding: 30,
        fontFamily: "Roboto",
        fontSize: 10,
        lineHeight: 1.4,
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: 30,
        alignItems: "center",
    },
    logo: {
        width: 150,
        height: 75,
        objectFit: "contain",
    },
    companyInfo: {
        marginBottom: 20,
        fontSize: 10,
        lineHeight: 1.4,
        textAlign: "left",
    },
    companyInfoCompanyVAT: {
        marginTop: 10,
        marginBottom: 10,
    },
    title: {
        fontSize: 16,
        fontFamily: "Roboto",
        fontWeight: 700,
        textAlign: "center",
        marginBottom: 16,
        textDecoration: "underline",
    },
    invoiceDetails: {
        marginBottom: 16,
        fontSize: 10,
        lineHeight: 1.5,
        textAlign: "center",
    },
    invoiceDetailsText: {
        // marginBottom: 10,
        // marginTop: 10,
        fontSize: 10,
        lineHeight: 1.5,
        textAlign: "center",
        width: 375,
        marginHorizontal: "auto",
    },
    propertyLocation: {
        marginBottom: 20,
        fontSize: 10,
        textAlign: "center",
        fontFamily: "Roboto",
    },
    services: {
        marginBottom: 16,
        fontSize: 10,
        textAlign: "center",
    },
    serviceItem: {
        marginBottom: 5,
        paddingVertical: 2,
    },
    table: {
        marginLeft: 65,
        maxWidth: 400,
        marginTop: 10,
        marginBottom: 10,
        borderWidth: 1,
        borderColor: "#000",
        borderStyle: "solid",
    },
    tableRow: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderBottomColor: "#000",
        fontSize: 10,
    },
    tableHeader: {
        fontFamily: "Roboto",
        fontWeight: 700,
    },
    tableCell: {
        flex: 2,
        textAlign: "left",
        paddingHorizontal: 8,
        borderRightWidth: 1,
        borderRightColor: "#000",
    },
    tableCellAmount: {
        flex: 1,
        textAlign: "right",
        paddingHorizontal: 8,
    },
    vatNotice: {
        fontFamily: "Roboto",
        fontSize: 10,
        marginTop: 15,
        marginBottom: 0,
        textAlign: "left",
        fontStyle: "italic",
    },
    signatureStampWrapper: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
    },
    signature: {
        marginTop: 40,
        marginRight: 40,
        borderTopWidth: 1,
        borderTopColor: "#000",
        width: 200,
        fontSize: 9,
        textAlign: "center",
        paddingTop: 8,
    },
    stamp: {
        marginTop: 30,
    },
    footer: {
        fontSize: 8,
        textAlign: "center",
        lineHeight: 1.3,
        position: "absolute",
        bottom: 30,
        left: 0,
        right: 0,
        margin: 0,
        padding: 0,
        paddingHorizontal: 30,
    },
    commissionText: {
        marginBottom: 20,
        fontSize: 10,
        textAlign: "center",
        fontFamily: "Roboto",
    },
    bold: {
        fontFamily: "Roboto",
        fontWeight: 700,
    },
    stampImage: {
        width: 100,
        height: 100,
        objectFit: "contain",
        marginTop: -40,
        marginLeft: -10,
        opacity: 1,
        fontFamily: "Roboto",
    },
});

// Format currency with proper decimal places
const formatCurrency = (amount) => {
    if (typeof amount !== "number") return "0.00";
    const formatted = new Intl.NumberFormat("sl-SI", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
    return `${formatted}`;
};

const PreInvoicePDF = ({ data }) => {
    // Validate required data
    if (!data) {
        throw new Error("Invoice data is required");
    }

    const language = data.language || "SI";

    // Translations with ASCII characters
    const translations = {
        SI: {
            title: "PREDRAČUN",
            invoiceNo: "Številka predračuna",
            // reference: "Referenca",
            placeAndDate: "Kraj in datum izdaje predračuna",
            // serviceDate: "Datum opravljene storitve",
            paymentTime: "Rok plačila",
            propertyLocationProdaja:
                "Posredovanje pri prodaji nepremičnine na naslovu",
            propertyLocationOddaja:
                "Posredovanje pri oddaji nepremičnine na naslovu",
            propertyLocationOglasevanje:
                "Storitve oglaševanja za nepremičnine na naslovu",
            propertyLocationDelDodatnihStoritev:
                "Del dodatnih storitev v skladu s točko 2 cenika Splošnih pogojev poslovanja podjetja Stoja Trade pri nepremičninskih poslih:",
            services: "Opravljene storitve",
            amountWithoutVAT: "Vrednost brez DDV",
            discount: "Popust",
            subtotal: "Vmesni znesek",
            vat: "DDV",
            totalAmount: "Znesek za plačilo z DDV",
            vatNotice:
                "Po ZDDV smo registrirani, kot OBVEZNI davčni zavezanci.",
            signature: "podpis odgovorne osebe",
            stamp: "žig",
            days: "dni",
            bank: "Banka",
            code: "koda",
            racun: "Račun",
            footerText:
                "Stoja Trade d.o.o., Ljubljana, Dolenjska cesta 242c, 1000 Ljubljana; Matična št.: 3540798000, ID za DDV: SI 56771803; UniCredit Banka: SI56 2900 0005 5236 604",
            commissionText: "Provizija:",
            invoiceDetailsText1: "UniCredit Banka Slovenija d.d., Ljubljana,",
            DDVNumber: "ID št. za DDV",
        },
        EN: {
            title: "Proforma Invoice",
            invoiceNo: "Proforma Invoice no.",
            // reference: "Reference no.",
            placeAndDate: "Place and date of issued proforma invoice",
            // serviceDate: "Date of provided services",
            paymentTime: "Payment time",
            propertyLocationProdaja: "Sale of real estate at the address",
            propertyLocationOddaja: "Renting real estate at the address",
            propertyLocationOglasevanje:
                "Advertising services for real estate at the address",
            propertyLocationDelDodatnihStoritev:
                "Additional services, in accordance with point 2 of the price list of the General Terms and Conditions in Real Estate transactions = ",
            services: "Provided services",
            amountWithoutVAT: "Amount without VAT",
            discount: "Discount",
            subtotal: "Subtotal",
            vat: "VAT",
            totalAmount: "Total amount with VAT",
            vatNotice: "We are registered as mandatory VAT taxpayers.",
            signature: "authorized signature",
            stamp: "stamp",
            days: "days",
            bank: "Bank",
            code: "code",
            racun: "Bank account No.",
            footerText:
                "Stoja Trade d.o.o., Ljubljana, Dolenjska cesta 242c, 1000 Ljubljana; Registration number: 3540798000, ID for DDV: SI 56771803; UniCredit Bank: SI56 2900 0005 5236 604",
            commissionText: "Commission:",
            invoiceDetailsText1: "UniCredit Bank Slovenia d.d., Ljubljana,",
            DDVNumber: "ID number for DDV",
        },
    };

    const t = translations[language] || translations.SI;

    // Default values for missing data
    const invoiceData = {
        companyName: data.companyName || "Stoja Trade d.o.o.",
        companyAddress: data.companyAddress || "Dolenjska cesta 242c",
        companyCity: data.companyCity || "1000 Ljubljana, Slovenija",
        companyRegNumber: data.companyRegNumber || "3540798000",
        companyVAT: data.companyVAT || "",
        companyEmail: data.companyEmail || "",
        companyPhone: data.companyPhone || "",
        invoiceNumber: data.invoiceNumber || "N/A",
        // reference: data.reference || "N/A",
        issueDate: data.issueDate || new Date().toLocaleDateString("sl-SI"),
        // serviceDate: data.serviceDate || new Date().toLocaleDateString("sl-SI"),
        propertyAddress: data.propertyAddress || "",
        services: data.services || [],
        amountWithoutVAT: data.amountWithoutVAT || 0,
        discountPercentage: data.discountPercentage || 0,
        discountAmount: data.discountAmount || 0,
        subtotal: data.subtotal || 0,
        vatRate: data.vatRate || 22,
        vatAmount: data.vatAmount || 0,
        totalWithVAT: data.totalWithVAT || 0,
        paymentDays: data.paymentDays || 0,
        paymentDate: data.paymentDate || "",
        billType: data.billType || "oddaja",
        additional_services: data.additional_services || 0,
        agent: data.agent,
    };

    // Get the appropriate property location text based on bill type
    const getPropertyLocationText = () => {
        switch (invoiceData.billType) {
            case "prodaja":
                return `${t.propertyLocationProdaja}: ${invoiceData.propertyAddress}`;
            case "oddaja":
                return `${t.propertyLocationOddaja}: ${invoiceData.propertyAddress}`;
            case "oglasevanje":
                return `${t.propertyLocationOglasevanje}: ${invoiceData.propertyAddress}`;
            case "del_dodatnih_storitev":
                return `${t.propertyLocationDelDodatnihStoritev} ${invoiceData.amountWithoutVAT}€ + ${t.vat}`;
            default:
                return t.propertyLocationOddaja;
        }
    };

    const formatEUR = (num) => {
        const formatted = new Intl.NumberFormat("sl-SI", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(num);
        return `EUR: ${formatted}`;
    };

    // Get the appropriate commission text based on bill type
    const getCommissionText = () => {
        switch (invoiceData.billType) {
            case "prodaja":
                return `${t.commissionText} ${formatEUR(
                    invoiceData.totalWithVAT
                )}€ + ${t.vat}`;
            case "oddaja":
                if (
                    invoiceData.additional_services ==
                    invoiceData.amountWithoutVAT
                ) {
                    return `1. ${
                        t.propertyLocationDelDodatnihStoritev
                    } ${formatEUR(invoiceData.additional_services)}€ + ${
                        t.vat
                    }`;
                }

                if (invoiceData.additional_services > 0) {
                    if (invoiceData.amountWithoutVAT > 0) {
                        return `1. ${t.commissionText} ${formatEUR(
                            invoiceData.amountWithoutVAT -
                                invoiceData.additional_services
                        )}€ + ${t.vat}
                       2. ${t.propertyLocationDelDodatnihStoritev} ${formatEUR(
                            invoiceData.additional_services
                        )}€ + ${t.vat}`;
                    } else {
                        return `1. ${
                            t.propertyLocationDelDodatnihStoritev
                        } ${formatEUR(invoiceData.additional_services)}€ + ${
                            t.vat
                        }`;
                    }
                } else {
                    if (invoiceData.amountWithoutVAT > 0) {
                        return `1. ${t.commissionText} ${formatEUR(
                            invoiceData.amountWithoutVAT -
                                invoiceData.additional_services
                        )}€ + ${t.vat}`;
                    } else {
                        return null;
                    }
                }
            default:
                return null;
        }
    };

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* Header with logos */}
                <View style={styles.header}>
                    <Image src={stojaLogo} style={styles.logo} />
                    <Image src={certificationLogo} style={styles.logo} />
                    <Image src={dqsLogo} style={styles.logo} />
                </View>

                {/* Company Info */}
                <View style={styles.companyInfo}>
                    <Text style={styles.bold}>{invoiceData.companyName}</Text>
                    <Text>{invoiceData.companyAddress}</Text>
                    <Text>{invoiceData.companyCity}</Text>
                    {/* <Text>MS: {invoiceData.companyRegNumber}</Text> */}
                    {invoiceData.companyVAT.length > 0 && (
                        <Text style={styles.companyInfoCompanyVAT}>
                            {t.DDVNumber}: {invoiceData.companyVAT}
                        </Text>
                    )}
                    {invoiceData.companyEmail && (
                        <Text>E: {invoiceData.companyEmail}</Text>
                    )}
                    {invoiceData.companyPhone && (
                        <Text>T: {invoiceData.companyPhone}</Text>
                    )}
                </View>

                {/* Invoice Title */}
                <Text style={styles.title}>{`${t.title}`}</Text>

                {/* Invoice Details */}
                <View style={styles.invoiceDetails}>
                    <Text>
                        {t.invoiceNo}: {invoiceData.invoiceNumber}{" "}
                        {invoiceData.agent}
                    </Text>
                    {/* <Text>
                        {t.reference}:{" "}
                        {invoiceData.reference.split(" ").length > 2 &&
                        invoiceData.agent
                            ? `${invoiceData.reference.split(" ")[0]} ${
                                  invoiceData.reference.split(" ")[1]
                              } ${invoiceData.agent} ${
                                  invoiceData.reference.split(" ")[2]
                              }`
                            : invoiceData.reference}
                    </Text> */}
                    <Text>
                        {t.placeAndDate}: Ljubljana, {invoiceData.issueDate}
                    </Text>
                    {/* <Text>
                        {t.serviceDate}: {invoiceData.serviceDate}
                    </Text> */}
                    <Text style={styles.invoiceDetailsText}>
                        {t.bank}:{" "}
                        <Text style={styles.bold}>{t.invoiceDetailsText1}</Text>{" "}
                        <Text style={styles.bold}>
                            Šmartinska 132, Ljubljana, SLO
                        </Text>{" "}
                        {t.racun}:{" "}
                        <Text style={styles.bold}>29000-0055236604</Text> Swift{" "}
                        {t.code}: <Text style={styles.bold}>BACXSI22</Text> IBAN{" "}
                        {t.code}:{" "}
                        <Text style={styles.bold}>SI56 2900 0005 5236 604</Text>
                    </Text>
                    <Text>
                        {t.paymentTime}: {invoiceData.paymentDays} {t.days}
                    </Text>
                </View>

                {/* Property Location */}
                {invoiceData.propertyAddress &&
                    invoiceData.billType.length > 0 && (
                        <View style={styles.propertyLocation}>
                            <Text>{getPropertyLocationText()}</Text>
                        </View>
                    )}

                {/* Commission Text */}
                {invoiceData.billType === "prodaja" &&
                    invoiceData.totalWithVAT > 0 && (
                        <View style={styles.commissionText}>
                            <Text>{getCommissionText()}</Text>
                        </View>
                    )}
                {invoiceData.billType === "oddaja" &&
                    (invoiceData.totalWithVAT > 0 ||
                        invoiceData.additional_services > 0) && (
                        <View style={styles.commissionText}>
                            <Text>{getCommissionText()}</Text>
                        </View>
                    )}

                {/* Services */}
                {invoiceData.services.length > 0 && (
                    <View style={styles.services}>
                        {invoiceData.services.map((service, index) => (
                            <View key={index} style={styles.serviceItem}>
                                <Text>{service.description}</Text>
                            </View>
                        ))}
                    </View>
                )}

                {/* Price Table */}
                <View style={styles.table}>
                    <View style={[styles.tableRow]}>
                        <Text style={styles.tableCell}>
                            {t.amountWithoutVAT}
                        </Text>
                        <Text style={styles.tableCellAmount}>
                            {formatCurrency(invoiceData.amountWithoutVAT)} €
                        </Text>
                    </View>

                    {invoiceData.discountPercentage > 0 &&
                        invoiceData.discountAmount > 0 && (
                            <View style={styles.tableRow}>
                                <Text style={styles.tableCell}>
                                    {t.discount}{" "}
                                    {invoiceData.discountPercentage}%
                                </Text>
                                <Text style={styles.tableCellAmount}>
                                    -
                                    {formatCurrency(invoiceData.discountAmount)}{" "}
                                    €
                                </Text>
                            </View>
                        )}
                    {invoiceData.discountPercentage > 0 &&
                        invoiceData.discountAmount > 0 && (
                            <View style={[styles.tableRow, styles.tableHeader]}>
                                <Text style={styles.tableCell}>
                                    {t.subtotal}
                                </Text>
                                <Text style={styles.tableCellAmount}>
                                    {formatCurrency(
                                        invoiceData.subtotal -
                                            invoiceData.discountAmount
                                    )}{" "}
                                    €
                                </Text>
                            </View>
                        )}

                    <View style={styles.tableRow}>
                        <Text style={styles.tableCell}>
                            {t.vat} {invoiceData.vatRate}%
                        </Text>
                        <Text style={styles.tableCellAmount}>
                            {formatCurrency(invoiceData.vatAmount)} €
                        </Text>
                    </View>

                    <View
                        style={[
                            styles.tableRow,
                            styles.tableHeader,
                            { borderBottomWidth: 0 },
                        ]}
                    >
                        <Text style={styles.tableCell}>{t.totalAmount}</Text>
                        <Text style={styles.tableCellAmount}>
                            {formatCurrency(invoiceData.totalWithVAT)} €
                        </Text>
                    </View>
                </View>

                {/* VAT Notice */}
                <Text style={styles.vatNotice}>{t.vatNotice}</Text>

                {/* Signature & Stamp */}
                <View style={styles.signatureStampWrapper}>
                    <View style={styles.signature}>
                        <Text>{t.signature}</Text>
                    </View>
                    <Text style={styles.stamp}>{t.stamp}</Text>
                    <Image src={stojaStamp} style={styles.stampImage} />
                </View>

                {/* Footer */}
                <View style={styles.footer}>
                    <Text>{t.footerText}</Text>
                </View>
            </Page>
        </Document>
    );
};

export default PreInvoicePDF;
